import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { uploadDocument, type DocumentCreate, type DocumentRow, type UploadUrlRequest } from "@/lib/api";
import { useToast } from "./Toast";
import { Button } from "./ui";

/**
 * Uploads straight to Supabase Storage with a signed URL (§9 Phase 3), then records the row.
 * The browser never holds the service key and the bytes never touch the API.
 */
export function FileUpload({
  entityType,
  entityId,
  kind = "other",
  label = "Upload",
  onUploaded,
  testId,
}: {
  entityType: UploadUrlRequest["entity_type"];
  entityId: string;
  kind?: DocumentCreate["kind"];
  label?: string;
  onUploaded?: (doc: DocumentRow) => void | Promise<void>;
  testId?: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      setProgress(`Uploading ${file.name}…`);
      return uploadDocument(file, { entity_type: entityType, entity_id: entityId, kind });
    },
    onSuccess: async (doc) => {
      setProgress(null);
      toast.push(`${doc.filename} uploaded`);
      await onUploaded?.(doc);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => {
      setProgress(null);
      toast.push(e.message, "danger");
    },
  });

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid={testId ? `${testId}-input` : "file-input"}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = "";
        }}
      />
      <Button
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        data-testid={testId}
      >
        {upload.isPending ? "Uploading…" : label}
      </Button>
      {progress && <span className="text-[11px] text-fg-muted">{progress}</span>}
    </div>
  );
}

export function DownloadLink({ documentId, filename }: { documentId: string; filename: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="underline"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void (async () => {
          try {
            const { api } = await import("@/lib/api");
            const { url } = await api.downloadUrl(documentId);
            window.open(url, "_blank", "noopener");
          } catch (e) {
            toast.push(e instanceof Error ? e.message : String(e), "danger");
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      {filename}
    </button>
  );
}
